import React, { memo, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import CustomInput from 'src/components/common/customInput';
import CustomRadioGroup from 'src/components/common/customRadio';
import lodashCamelcase from 'lodash/camelCase';
import { CloseCircleFilled, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import config from 'src/config';
import { AiSqlModal } from './AskAI/AiSqlModal';
import { AskAiButton } from './AskAI/AskAiButton';
import useLazyAPI from 'src/components/common/hooks/useLazyAPI';
const { AI_ANALYTICS } = config;

const useStyle = createUseStyles({
    basePane: {
        backgroundColor: '#FAF7EE',
        padding: '8px',
        boxShadow: '0 0 10px 0 rgba(0,0,0,0.15)',
        marginBottom: '10px',
        position: 'relative',
    },
    closeButton: {
        position: 'absolute',
        top: '10px',
        right: '10px',
    },
});

const convertPrettyNameToMetric = (string) => {
    return lodashCamelcase(string
        .toLowerCase()
        .split('')
        .map(c => {
            if ((c >= 'a' && c <= 'z') || (c >= '0' && c <= '9') || c === ' ') {
                return c;
            }
            return c.charCodeAt(0);  // Convert special char to its ASCII code
        })
        .join(''));
};

const CustomColumnsDataCard =   (props) => {
    const { t } = useTranslation();
    const { onChange, data, onRemove, selectedReportType } = props;
    const classes = useStyle();
    const [prettyName, setPrettyName] = useState(data.prettyName);
    const [type, setType] = useState(data.type);
    const [sql, setSql] = useState(data.sql);
    const [showAiInput, setShowAiInput] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiResponse, setAiResponse] = useState<{ column_name: string, format: string, sql: string } | null>(null);

    const typeOptions = [
        {value: 'number', prettyName: t('Number')},
        {value: 'text', prettyName: t('Text')},
        {value: 'boolean', prettyName: t('Boolean')},
        {value: 'timestampz', prettyName: t('Timestamp')},
        {value: 'date', prettyName: t('Date')},
    ];

    useEffect(() => {
         const { prettyName, type, sql } = data;
         setPrettyName(prettyName);
         setType(type);
         setSql(sql);
     }, [data]);

    useEffect(() => {
        // For existing columns: keep the original name to avoid breaking references
        // For new columns: don't set name until user finishes typing (onBlur or similar)
        const newName = data.name ? data.name : '';
        onChange({
            name: newName,
            prettyName,
            type,
            sql,
            id: data.id,
        });
    }, [prettyName, type, sql]);
    // if you select any text in sqlInputEditor and paste after selection then onChange will be called
    // twice once for cut and second for paste and setState will stuck in a infinite loop
    // to prevent this problem
    const lazyUpdate = () => {
        let sqlValue = '';
        let inside = false;
        return (value) => {
            sqlValue = value;
            if(inside === false){
                inside = true;
                setTimeout(() => {
                    setSql(sqlValue);
                    inside = false;
                });
            }
        };
    };

    const [askAI, { loading: isLoadingAI }] = useLazyAPI(AI_ANALYTICS.URL, {
        type: 'POST',
        onCompleted: (result) => {
            setAiResponse(result);
        },
        onError: (error) => {
            console.error('Error asking AI:', error);
        },
        baseURL: AI_ANALYTICS.BASE_URL,
    });

    const handleAskAI = async () => {
        askAI({
            variables: { 
                query: `${aiPrompt}\n${ sql ? `Current SQL: ${sql}` : ''}`,
                type: AI_ANALYTICS.TYPE.replace('{{selectedReportType}}', selectedReportType)
                    .replace('{{customColumnType}}', props.customColumnType),
            },
        });
    };

    const handleAcceptAI = () => {
        if (!aiResponse) return;
        onChange({
            name: convertPrettyNameToMetric(prettyName || aiResponse.column_name),
            prettyName: prettyName || aiResponse.column_name,
            type: aiResponse.format,
            sql: aiResponse.sql,
            id: data.id,
        });
        
        setPrettyName(prettyName || aiResponse.column_name);
        setType(aiResponse.format);
        setSql(aiResponse.sql);
        setShowAiInput(false);
        setAiPrompt('');
        setAiResponse(null);
    };

    const handleRejectAI = () => {
        setAiResponse(null);
    };

    return <div className={classes.basePane}>
        <div className={classes.closeButton}>
            <CloseCircleFilled onClick={onRemove} style={{ cursor: 'pointer', fontSize: '16px', color: 'rgba(0,0,0,0.3)' }} />
        </div>
        
        <AskAiButton onClick={() => setShowAiInput(true)} />

        <AiSqlModal 
            isOpen={showAiInput}
            onClose={() => setShowAiInput(false)}
            onAccept={handleAcceptAI}
            aiPrompt={aiPrompt}
            setAiPrompt={setAiPrompt}
            aiResponse={aiResponse}
            isLoading={isLoadingAI}
            onAskAi={handleAskAI}
            onReject={handleRejectAI}
            showColumnName={prettyName ? false : true}
        />
        
        <CustomInput 
            onChange={setPrettyName} 
            onBlur={() => {
                // For new columns, set the name when user finishes typing
                if (!data.name && prettyName) {
                    const newName = convertPrettyNameToMetric(prettyName);
                    onChange({
                        name: newName,
                        prettyName,
                        type,
                        sql,
                        id: data.id,
                    });
                }
            }}
            prettyName={t('Pretty Name')} 
            value={prettyName} 
            placeholder={t('Column Name')}
        />
        <CustomRadioGroup onChange={setType} prettyName={t('Column Type')} value={type} data={{options:typeOptions}} direction={'horizontal'}/>
        <CustomInput onChange={lazyUpdate()} prettyName={t('Sql')} value={sql} placeholder={t('Please Input metric Sql')} isCodeEditor={true}/>
    </div>;
};

export default memo(CustomColumnsDataCard);
